import remoteOralExamActions from '../actions/RemoteOralExamActions';
import RemoteOralExamConstants from '../constants/RemoteOralExamConstants';
import sessionReader from '../store/readHelpers/SessionReader';
import storeManager from '../store/StoreManager';
import apiService from './api/ApiService';
import navigationService from './NavigationService';
import sessionStorageService from './SessionStorageService';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import utilService from './UtilService';
import durationTrackingService from './DurationTrackingService';
import responseService from './ResponseService';
import preCheckService from './PreCheckService';
import routeTypeService from './RouteTypeService';
import alertService from './AlertService';
import questionReader from '../store/readHelpers/QuestionReader';
import { reportError } from './ErrorReporting';
import liveDataLoader from '../loading/liveDataLoader';

const displayStatus = RemoteOralExamConstants.contextDisplayStatus;
const interval = 20000;
let fetchSessionInfoInterval = null;

const remoteOralExamService = {
    scheduleFetchingOfSessionInfo() {
        if (fetchSessionInfoInterval) {
            this.cancelScheduledFetching();
        }
        this.fetchAndProcessSessionInfo();
        fetchSessionInfoInterval = setInterval(this.fetchAndProcessSessionInfo.bind(this), interval);
    },

    cancelScheduledFetching() {
        clearInterval(fetchSessionInfoInterval);
        fetchSessionInfoInterval = null;
    },

    fetchAndProcessSessionInfo() {
        if (!preCheckService.allShownChecksCompleted()) {
            console.warn('RemoteOralExamSessionInfo must not be fetched until all pre-checks are completed');
            return;
        }

        apiService.fetchRemoteOralExamSessionInfo().then(async (fetchedSessionInfo) => {
            const store = storeManager.getStore();
            const {
                currentlyShown: { routeType },
                remoteOralExam: { sessionInfo: previousSessionInfo } } = storeManager.getState();

            store.dispatch(remoteOralExamActions.setSessionInfo(fetchedSessionInfo));

            this.checkIfSessionBecameCompleted(previousSessionInfo.sessionStatus, fetchedSessionInfo.sessionStatus);
            await this.checkIfGetQuestionSetNeeded(fetchedSessionInfo, routeType);
            this.checkIfItemResultReportNeeded(previousSessionInfo.contextObjectId, fetchedSessionInfo, routeType);
            this.checkIfNavigationNeeded(fetchedSessionInfo, routeType);
            this.checkQuestionVisibility(previousSessionInfo, fetchedSessionInfo);
            this.checkDisplayStatusAcknowledge(previousSessionInfo, fetchedSessionInfo);
        });
    },

    getSessionInfo() {
        return apiService.fetchRemoteOralExamSessionInfo().then((fetchedSessionInfo) => {
            const { remoteOralExam } = storeManager.getState();
            if (this.candidateInProgress(remoteOralExam)) {
                this.checkIfItemResultReportNeeded(null, fetchedSessionInfo);
            }

            const store = storeManager.getStore();
            // for test api response correctly, will be removed.
            console.log(`Test api GetRemoteOralExamResponse:${JSON.stringify(fetchedSessionInfo)}`);
            store.dispatch(remoteOralExamActions.setSessionInfo(fetchedSessionInfo));
        });
    },

    getZoomTestCallDetail() {
        if (sessionReader.isZoomHuichangEnabled()) {
            return apiService.getZoomHuichangTestCallDetail().then((testDetail) => {
                const store = storeManager.getStore();
                store.dispatch(remoteOralExamActions.setZoomTestCallDetail(testDetail));
            });
        }
        return apiService.getZoomTestCallDetail().then((testDetail) => {
            const store = storeManager.getStore();
            store.dispatch(remoteOralExamActions.setZoomTestCallDetail(testDetail));
        });
    },

    isDisplayStatusAcknowledgeNeeded(newDisplayStatus, newSessionInfoReceived) {
        const {
            currentlyShown: { routeType },
            remoteOralExam: {
                sessionInfo,
                sessionInfo: { contextDisplayStatus },
                contextDisplayStatusAcknowledgeSent,
            } } = storeManager.getState();


        // sendDisplayStatusAcknowledge was called not from the Topic callback but on GetRemoteOralExamSessionInfo response
        // and current page is remote-oral
        // as result - do not send acknowledge yet because it will be sent form the Topic callbacks
        if (newSessionInfoReceived && routeTypeService.isRemoteOralPage(routeType)) {
            return false;
        }

        // do not send acknowledge when new status is not defined or not changed or acknowledge was already sent
        if (!newDisplayStatus || newDisplayStatus === contextDisplayStatus || contextDisplayStatusAcknowledgeSent) {
            return false;
        }

        // https://inspera.atlassian.net/wiki/spaces/TESTC/pages/3926950305/SPEC+RS+-+Use+Case+124
        // use case E: do not send hide acknowledge when TT opens the menu (Topic unmount callback is called because of it)
        if (newDisplayStatus === displayStatus.CONTEXT_HIDE_ACK && remoteOralExamService.isDisplayStatusVisible(sessionInfo)) {
            return false;
        }

        // https://inspera.atlassian.net/wiki/spaces/TESTC/pages/3926950305/SPEC+RS+-+Use+Case+124
        // use case F: do not send display acknowledge when TT closes the menu (Topic mount callback is called because of it)
        if (newDisplayStatus === displayStatus.CONTEXT_DISPLAY_ACK && remoteOralExamService.isDisplayStatusHidden(sessionInfo)) {
            return false;
        }

        return true;
    },

    /**
     * There 2 scenarios when sendDisplayStatusAcknowledge is called:
     * 1. Topic component mount/unmount.
     *    In this case we check if we need to sent acknowledge because this component is also mount/unmount when TT opens/closes the menu
     * 2. New SessionInfo came from api request.
     *    In this case we check current page and if it's not remote-oral then we also check if acknowledge needed
     */
    sendDisplayStatusAcknowledge(newDisplayStatus, newSessionInfoReceived = false) {
        if (this.isDisplayStatusAcknowledgeNeeded(newDisplayStatus, newSessionInfoReceived)) {
            apiService.sendRemoteOralExamStatus(sessionReader.getAssessmentRunId(), newDisplayStatus);
            const store = storeManager.getStore();
            store.dispatch(remoteOralExamActions.setContextDisplayStatusAcknowledgeSent(true));
        }
    },

    displayStatusBecameVisible(previousSessionInfo, sessionInfo) {
        console.log(previousSessionInfo.contextDisplayStatus, this.isDisplayStatusHidden(previousSessionInfo));
        return this.isDisplayStatusHidden(previousSessionInfo) && this.isDisplayStatusVisible(sessionInfo);
    },

    displayStatusBecameHidden(previousSessionInfo, sessionInfo) {
        return this.isDisplayStatusVisible(previousSessionInfo) && this.isDisplayStatusHidden(sessionInfo);
    },

    isDisplayStatusVisible({ contextDisplayStatus } = {}) {
        return [
            displayStatus.CONTEXT_DISPLAY_REQ,
            displayStatus.CONTEXT_DISPLAY_ACK,
        ].includes(contextDisplayStatus);
    },

    isDisplayStatusHidden({ contextDisplayStatus } = {}) {
        return !contextDisplayStatus || [
            displayStatus.CONTEXT_HIDE_REQ,
            displayStatus.CONTEXT_HIDE_ACK,
        ].includes(contextDisplayStatus);
    },

    isTestCompleted(remoteOralExam = {}) {
        return [RemoteOralExamConstants.sessionStatus.FINISHED,
            RemoteOralExamConstants.sessionStatus.CLOSED].includes(remoteOralExam?.sessionInfo?.sessionStatus)
            || this.isSessionCompleted(remoteOralExam);
    },

    isWaiting({ sessionStatus } = {}) {
        return !sessionStatus || sessionStatus === RemoteOralExamConstants.sessionStatus.CREATED;
    },

    isInProgress({ sessionStatus } = {}) {
        return sessionStatus === RemoteOralExamConstants.sessionStatus.IN_PROGRESS;
    },

    isCandidateJoined({ isCandidateJoined } = {}) {
        return !!isCandidateJoined
            || utilService.toBoolean(sessionStorageService.getItem(RemoteOralExamConstants.sessionStorageKeys.JOINED));
    },

    isSessionCompleted({ isSessionCompleted } = {}) {
        return !!isSessionCompleted
            || utilService.toBoolean(sessionStorageService.getItem(RemoteOralExamConstants.sessionStorageKeys.COMPLETED));
    },

    candidateInProgress(remoteOralExam = {}) {
        return preCheckService.allShownChecksCompleted()
            && this.isCandidateJoined(remoteOralExam)
            && this.isInProgress(remoteOralExam.sessionInfo);
    },

    getZoomCredentials({ externalResource } = {}) {
        const type = externalResource?.type;

        if (
            type === RemoteOralExamConstants.callTypes.zoom
            || type === RemoteOralExamConstants.callTypes.WebRTC
            || type === RemoteOralExamConstants.callTypes.zoom_huichang
            || type === RemoteOralExamConstants.callTypes.zoom_sdk_app
        ) {
            return externalResource;
        }

        return null;
    },

    setCandidateJoined() {
        const store = storeManager.getStore();
        store.dispatch(remoteOralExamActions.setCandidateJoined(true));
        sessionStorageService.setItem(RemoteOralExamConstants.sessionStorageKeys.JOINED, true);
    },

    setSessionCompleted() {
        this.cancelScheduledFetching();
        const store = storeManager.getStore();
        store.dispatch(remoteOralExamActions.setSessionCompleted(true));
        sessionStorageService.setItem(RemoteOralExamConstants.sessionStorageKeys.COMPLETED, true);
    },

    async checkIfGetQuestionSetNeeded(sessionInfo, routeType) {
        if (this.statusBecameInProgress(sessionInfo.sessionStatus, routeType)) {
            await liveDataLoader.loadQuestionSet();
            await liveDataLoader.loadSavedResponsesForExam();
            liveDataLoader.parseQuestions();
        }
    },

    async checkIfNavigationNeeded(sessionInfo, routeType) {
        if (this.isSessionCompleted(sessionInfo)) {
            if (utilService.isIep()) {
                await apiService.sendCompletedFakeRequest();
            }
            navigationService.navigateToTestOverview();
            return;
        }

        if (this.isCandidateJoined(sessionInfo)
            && this.statusBecameInProgress(sessionInfo.sessionStatus, routeType)) {
            navigationService.navigateToRemoteOral();
        }
    },

    checkQuestionVisibility(previousSessionInfo, sessionInfo) {
        const currentContextObjectId = sessionInfo.contextObjectId;
        if (!currentContextObjectId) {
            // do not anything if object id is not provided
            return;
        }

        if (!this.isDisplayStatusVisible(sessionInfo)) {
            // do not anything if content is hidden by author
            return;
        }

        if (assessmentReader.getSectionById(currentContextObjectId)) {
            // do not anything if object id is a section
            return;
        }

        const question = assessmentReader.getQuestion(currentContextObjectId);
        if (questionReader.hasVisibleContent(question)) {
            return;
        }

        if (previousSessionInfo.contextObjectId !== currentContextObjectId) {
            this.notifyAboutMissedContent(currentContextObjectId);
        } else if (this.displayStatusBecameVisible(previousSessionInfo, sessionInfo)) {
            this.notifyAboutMissedContent(currentContextObjectId);
        }
    },

    checkDisplayStatusAcknowledge(previousSessionInfo, sessionInfo) {
        if (this.displayStatusBecameVisible(previousSessionInfo, sessionInfo)) {
            remoteOralExamService.sendDisplayStatusAcknowledge(displayStatus.CONTEXT_DISPLAY_ACK, true);
        } else if (this.displayStatusBecameHidden(previousSessionInfo, sessionInfo)) {
            remoteOralExamService.sendDisplayStatusAcknowledge(displayStatus.CONTEXT_HIDE_ACK, true);
        }
    },

    checkIfItemResultReportNeeded(previousObjectId, sessionInfo, routeType) {
        const { contextObjectId } = sessionInfo;
        if (previousObjectId === contextObjectId) {
            if (this.isTestCompleted(sessionInfo) && assessmentReader.getQuestion(previousObjectId)) {
                responseService.updateItemResultOnNavigation(previousObjectId);
            }

            if (!this.statusBecameInProgress(sessionInfo.sessionStatus, routeType)) {
                return;
            }
        }

        // start tracking duration if session found
        const section = assessmentReader.getSectionById(contextObjectId);
        if (section) {
            section.questions.forEach(({ contentRevisionId }) => {
                durationTrackingService.setNewDurationReferenceTimestamp(contentRevisionId);
            });
            return;
        }

        // start tracking question duration if question is found
        if (assessmentReader.getQuestion(contextObjectId)) {
            durationTrackingService.setNewDurationReferenceTimestamp(contextObjectId);
        }

        // report duration if previous id was a section
        const previousSection = assessmentReader.getSectionById(previousObjectId);
        if (previousSection) {
            previousSection.questions.forEach(({ contentRevisionId }) => {
                responseService.updateItemResultOnNavigation(contentRevisionId);
            });

            return;
        }

        // report duration if previous id was a question
        if (assessmentReader.getQuestion(previousObjectId)) {
            responseService.updateItemResultOnNavigation(previousObjectId);
        }
    },

    checkIfSessionBecameCompleted(previousStatus, sessionStatus) {
        if (this.statusBecameClosed(previousStatus, sessionStatus) || this.statusBecameFinished(sessionStatus)) {
            this.setSessionCompleted();
        }
    },

    statusBecameInProgress(newStatus, routeType) {
        return routeTypeService.isTestOverviewPage(routeType) && (newStatus === RemoteOralExamConstants.sessionStatus.IN_PROGRESS);
    },

    statusBecameFinished(status) {
        const { isSessionCompleted } = storeManager.getState();
        return !isSessionCompleted && status === RemoteOralExamConstants.sessionStatus.FINISHED;
    },

    statusBecameClosed(previousStatus, newStatus) {
        // if previous status was set but the new one is unset, it means the session has been closed
        // if both previous and new statuses are unset (undefined or null), it means the session has not been created yet
        return !!previousStatus && !newStatus;
    },

    isZoomTestCallNotRequested({ zoomTestCallStatus }) {
        return zoomTestCallStatus === RemoteOralExamConstants.zoomTestCallStatus.NOT_REQUESTED;
    },

    isZoomTestCallConfirmed({ zoomTestCallStatus }) {
        return zoomTestCallStatus === RemoteOralExamConstants.zoomTestCallStatus.CONFIRMED;
    },

    isZoomTestCallFailed({ zoomTestCallStatus }) {
        return zoomTestCallStatus === RemoteOralExamConstants.zoomTestCallStatus.FAILED;
    },

    showZoomCall(remoteOralExam, routeType) {
        return preCheckService.allShownChecksCompleted()
            && this.candidateInProgress(remoteOralExam)
            && !routeTypeService.isTestOverviewPage(routeType);
    },

    notifyAboutMissedContent(contextObjectId) {
        alertService.showRemoteOralInvisibleContentWarning();
        reportError(`Examiner attempted to show content that is not visible: contextObjectId - ${contextObjectId}`);
    },
};

export default remoteOralExamService;
